import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Heart, Clock, Users, Play } from 'lucide-react';
import { cn } from '../lib/utils';

export const RecipeCard = ({ recipe, isFavorite, onToggleFavorite }) => {
  const { t, getLocalizedField, language } = useAuth();

  const name = getLocalizedField(recipe, 'name');
  const description = getLocalizedField(recipe, 'description');

  const getDifficultyLabel = (diff) => {
    const labels = {
      sk: { easy: 'Jednoduché', medium: 'Stredné', hard: 'Náročné' },
      cz: { easy: 'Jednoduché', medium: 'Střední', hard: 'Náročné' }
    };
    return labels[language]?.[diff] || diff;
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'easy': return 'bg-secondary/20 text-secondary-hover';
      case 'medium': return 'bg-accent-yellow/30 text-amber-700';
      case 'hard': return 'bg-primary/20 text-primary-hover';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <article 
      className="group relative bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300 border border-border/40 recipe-card"
      data-testid={`recipe-card-${recipe.recipe_id}`}
    >
      {/* Image */}
      <Link to={`/recipes/${recipe.recipe_id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=800'}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          {/* Difficulty badge */}
          <span className={cn(
            "absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium",
            getDifficultyColor(recipe.difficulty)
          )}>
            {getDifficultyLabel(recipe.difficulty)}
          </span>

          {/* Video indicator */}
          {recipe.video_url && (
            <span
              className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 text-white text-xs font-medium backdrop-blur-sm"
              data-testid={`recipe-video-badge-${recipe.recipe_id}`}
            >
              <Play className="w-3 h-3 fill-current" /> Video
            </span>
          )}
        </div>
      </Link>

      {/* Favorite button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          onToggleFavorite?.(recipe.recipe_id);
        }}
        className={cn(
          "absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200",
          isFavorite 
            ? "bg-primary text-white" 
            : "bg-white/90 text-muted-foreground hover:text-primary hover:bg-white"
        )}
        aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
        data-testid={`favorite-btn-${recipe.recipe_id}`}
      >
        <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
      </button>

      {/* Content */}
      <div className="p-5">
        <Link to={`/recipes/${recipe.recipe_id}`}>
          <h3 className="font-serif text-lg font-semibold text-text-heading line-clamp-2 group-hover:text-primary transition-colors">
            {name}
          </h3>
        </Link>
        
        {description && (
          <p className="text-muted-foreground text-sm mt-2 line-clamp-2">
            {description}
          </p>
        )}

        {/* Meta info */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{(recipe.prep_time || 0) + (recipe.cook_time || 0)} {t('minutes')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{recipe.servings} {t('portions')}</span>
          </div>
        </div>

        {/* Category badges */}
        {recipe.category && recipe.category.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {recipe.category.slice(0, 3).map((cat) => (
              <span 
                key={cat} 
                className="px-2 py-0.5 bg-muted rounded-full text-xs text-muted-foreground"
              >
                {cat}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export const RecipeCardSkeleton = () => (
  <div className="bg-white rounded-3xl overflow-hidden shadow-soft border border-border/40 animate-pulse">
    <div className="aspect-[4/3] bg-muted" />
    <div className="p-5 space-y-3">
      <div className="h-6 bg-muted rounded w-3/4" />
      <div className="h-4 bg-muted rounded w-full" />
      <div className="h-4 bg-muted rounded w-2/3" />
      <div className="flex gap-4 pt-4 border-t border-border/50">
        <div className="h-4 bg-muted rounded w-16" />
        <div className="h-4 bg-muted rounded w-16" />
      </div>
    </div>
  </div>
);
